// useDialog2.tsx
import { useState, ReactNode, useCallback, JSX } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress } from '@mui/material';

export interface DialogButton<T = any> {
  label: string;
  value: T;
  color?: 'primary' | 'secondary' | 'error' | 'success' | 'warning';
  autoFocus?: boolean;
  disabled?: boolean;
}

export interface DialogOptions<T = any> {
  title?: string;
  message?: ReactNode;
  buttons?: DialogButton<T>[];
  input?: boolean;
  defaultValue?: string;
  // Теперь функция может возвращать Promise<boolean> или boolean
  isValid?: (body: string) => boolean | Promise<boolean>; 
}

interface UseDialogReturn<T = any> {
  showDialog: (options: DialogOptions<T>) => Promise<T | string | null>;
  DialogComponent: JSX.Element;
}

export function useDialog<T = any>(): UseDialogReturn<T> {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<DialogOptions<T>>({});
  const [resolver, setResolver] = useState<((value: T | string | null) => void) | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  
  // Новые состояния для асинхронной валидации
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const showDialog = useCallback((opts: DialogOptions<T>) => {
    return new Promise<T | string | null>((resolve) => {
      setOptions(opts);
      setInputValue(opts.defaultValue || '');
      setHasError(false);
      setIsLoading(false);
      setResolver(() => resolve);
      setOpen(true);
    });
  }, []);

  const handleClose = (value: T | string | null) => {
    setOpen(false);
    resolver?.(value);
  };

  const handleButtonClick = async (btn: DialogButton<T>) => {
    // Если нажата отмена (value === null), закрываем сразу без валидации
    if (btn.value === null) {
      handleClose(null);
      return;
    }

    if (options.input && options.isValid) {
      setIsLoading(true);
      setHasError(false);
      
      try {
        // Дожидаемся ответа от валидатора (работает и с async, и с обычными функциями)
        const valid = await options.isValid(inputValue);
        
        if (valid) {
          handleClose(inputValue);
        } else {
          setHasError(true); // Показываем ошибку, диалог НЕ закрывается
        }
      } catch (error) {
        console.error("Validation error:", error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Обычный prompt без валидации или простой диалог
      handleClose(options.input ? inputValue : btn.value);
    }
  };

  const DialogComponent = (
    <Dialog
      open={open}
      onClose={() => !isLoading && handleClose(null)} // Запрещаем закрывать по клику мимо во время загрузки
      aria-labelledby="dialog-title"
      fullWidth
      maxWidth="xs"
    >
      {options.title && <DialogTitle id="dialog-title">{options.title}</DialogTitle>}
      
      {(options.message || options.input) && (
        <DialogContent>
          {options.message && <div style={{ marginBottom: options.input ? 16 : 0 }}>{options.message}</div>}
          {options.input && (
            <TextField
              autoFocus
              fullWidth
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                if (hasError) setHasError(false); // Сбрасываем ошибку при вводе нового текста
              }}
              variant="outlined"
              margin="dense"
              error={hasError} // Красная рамка, если валидация провалена
              disabled={isLoading} // Блокируем инпут во время запроса
              helperText={hasError ? "Некоректні дані або значення вже зайняте" : ""}
            />
          )}
        </DialogContent>
      )}

      <DialogActions>
        {options.buttons?.map((btn, idx) => {
          // Во время загрузки блокируем все кнопки
          const isBtnDisabled = btn.disabled || isLoading;

          return (
            <Button
              key={idx}
              onClick={() => handleButtonClick(btn)}
              color={btn.color || 'primary'}
              autoFocus={btn.autoFocus}
              disabled={isBtnDisabled}
              startIcon={isLoading && btn.value !== null ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {btn.label}
            </Button>
          );
        })}
      </DialogActions>
    </Dialog>
  );

  return { showDialog, DialogComponent };
}